import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { getNumberOfProductsSEO } from 'utils/functions';
import { useRouter } from 'next/router';
import { getBaseUrl, getCanonicalUrl } from 'utils/url-query';

const newInPageSeoDefaultPropProducts = [];

const NewInPageSeo = ({ products = newInPageSeoDefaultPropProducts, url = 'https://www.creoate.com' }) => {
  const seoTitle = `New Arrivals - Discover and Buy New-in Products and Brands at Creoate`;
  const seoDescription = `Shop new in products online at wholesale prices from Creoate. Browse new brands and buy over ${getNumberOfProductsSEO()} new-in products in bulk from independent wholesale vendors and suppliers of UK, Europe and US.`;

  const router = useRouter();
  const { asPath } = router;

  const canonical = getCanonicalUrl(url + asPath);
  const baseUrl = getBaseUrl(url + asPath);

  const itemListElement = products?.map((product, idx) => ({
    type: 'ListItem',
    position: idx + 1,
    url: `${baseUrl}/product/${product.productSlug}`,
    name: product.productName,
  }));

  const listItemJson = {
    '@context': 'https://schema.org',
    '@type': 'itemList',
    itemListElement,
  };

  const SpeakableSchemaJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    name: 'New-Ins',
    speakable: {
      '@type': 'SpeakableSpecification',
      xPath: ['/html/head/title', "/html/head/meta[@name='description']/@content"],
    },
    url: canonical,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'New-Ins',
        item: canonical,
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />

        <meta property="og:locale" content="en_GB" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://www.creoate.com/new-in" />
        <meta property="og:site_name" content="CREOATE" />
        <meta
          property="og:image"
          content="https://creoateprod.s3.eu-west-2.amazonaws.com/wp-content/uploads/2020/03/05121722/cropped-final-logo-black-2.png"
        />
        <meta property="og:image:width" content="640" />
        <meta property="og:image:height" content="75" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:site" content="@creoateuk" />
        <meta name="twitter:creator" content="@creoateuk" />
        <link rel="canonical" href={canonical} />

        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      </Head>

      <script
        id="seo-script-new-ListItem"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbJsonLd),
        }}
      />

      <script
        id="seo-script-WebPage"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(SpeakableSchemaJsonLd),
        }}
      />

      <script
        id="seo-script-ItemList"
        type="application/ld+json"
        data-turbolinks-track="reload"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(listItemJson),
        }}
      />
    </>
  );
};

NewInPageSeo.propTypes = { products: PropTypes.instanceOf(Array) };

export default NewInPageSeo;
